import { Alert, Button, Chip, Stack, Typography } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { UrlUpdateType } from "use-query-params";
import { formatUTCDateWithLocalTime } from "../../../utils";
import {
  getMatchResolutionRequests,
  getMatchResolutionStatusLabel
} from "../shared";
import {
  BlacklistEntityType,
  BlacklistMatchResolutionRequest,
  BlacklistMatchResolutionRequestStatus
} from "../types";
import ReviewMatchResolutionRequestModal from "./ReviewMatchResolutionRequestModal";

const getEntityTypeLabel = (entityType: BlacklistEntityType): string => {
  switch (entityType) {
    case BlacklistEntityType.Person:
      return "Individual";
    case BlacklistEntityType.Company:
      return "Entity";
    default:
      return "Unknown";
  }
};

const getStatusColor = (status: BlacklistMatchResolutionRequestStatus): "default" | "success" | "warning" | "error" => {
  switch (status) {
    case BlacklistMatchResolutionRequestStatus.Pending:
      return "warning";
    case BlacklistMatchResolutionRequestStatus.Approved:
      return "success";
    case BlacklistMatchResolutionRequestStatus.Rejected:
      return "error";
    default:
      return "default";
  }
};

interface MatchResolutionRequestsTabProps {
  selectedRequestId?: number;
  setSelectedRequestId: (newValue: string | null | undefined, updateType?: UrlUpdateType) => void;
}

const MatchResolutionRequestsTab = ({ selectedRequestId, setSelectedRequestId }: MatchResolutionRequestsTabProps) => {
  const [requests, setRequests] = useState<BlacklistMatchResolutionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchRequests = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await getMatchResolutionRequests();
      setRequests(data);
    } catch (error) {
      console.error("Error fetching match resolution requests", error);
      setLoadError("There was a problem loading resolved contact requests.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const selectedRequest = useMemo(
    () => requests.find((request) => request.id === selectedRequestId) ?? null,
    [requests, selectedRequestId]
  );

  const handleOpenRequest = (requestId: number) => {
    setSelectedRequestId(String(requestId));
  };

  const handleCloseRequest = () => {
    setSelectedRequestId(undefined);
  };

  const columns: MRT_ColumnDef<BlacklistMatchResolutionRequest>[] = [
    {
      accessorKey: "status",
      header: "Status",
      size: 80,
      Cell: ({ row }) => (
        <Chip
          size="small"
          color={getStatusColor(row.original.status)}
          label={getMatchResolutionStatusLabel(row.original.status)}
        />
      )
    },
    {
      accessorKey: "lookupCode",
      header: "Lookup Code",
      size: 120
    },
    {
      accessorKey: "entityType",
      header: "Blacklist Type",
      size: 120,
      Cell: ({ row }) => <Typography variant="body2">{getEntityTypeLabel(row.original.entityType)}</Typography>
    },
    {
      accessorKey: "entityId",
      header: "Blacklist ID",
      size: 90
    },
    {
      accessorKey: "reason",
      header: "Reason",
      size: 350
    },
    {
      accessorKey: "requestedBy",
      header: "Requested By",
      size: 180
    },
    {
      accessorKey: "requestedDate",
      header: "Requested Date",
      accessorFn: (row) => (row.requestedDate ? formatUTCDateWithLocalTime(row.requestedDate) : null),
      size: 160
    },
    {
      accessorKey: "reviewedBy",
      header: "Reviewed By",
      size: 180
    },
    {
      accessorKey: "reviewedDate",
      header: "Reviewed Date",
      accessorFn: (row) => (row.reviewedDate ? formatUTCDateWithLocalTime(row.reviewedDate) : null),
      size: 160
    },
    {
      id: "actions",
      header: "Actions",
      size: 220,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const request = row.original;

        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => handleOpenRequest(request.id)}>
              Open
            </Button>
          </Stack>
        );
      }
    }
  ];

  return (
    <>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <MaterialReactTable
        enableStickyHeader
        columns={columns}
        data={requests}
        initialState={{ sorting: [{ id: "requestedDate", desc: true }] }}
        state={{
          isLoading
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: { backgroundColor: row.index % 2 === 0 ? "#f4f4f4" : "white" }
        })}
      />

      {selectedRequest && (
        <ReviewMatchResolutionRequestModal
          open={Boolean(selectedRequest)}
          onClose={handleCloseRequest}
          request={selectedRequest}
          onActionComplete={fetchRequests}
        />
      )}
    </>
  );
};

export default MatchResolutionRequestsTab;
